import { friendlyDbError } from '../../../utils/friendlyDbError';
import type { StateCreator } from 'zustand';
import type {
  SendSlice,
  WhatsAppChatStore,
  Message,
  PendingMessage,
  MessageType,
} from '../types';
import type { WahaSendResult } from '../../../types/whatsappChat';
import { supabase } from '../../../supabase';
import { getWahaService } from '../../../services/wahaApiService';
import { applyMessageToChats } from '../helpers';
import { notifyIfWindowClosed } from '../../../services/whatsapp/cloudProxyError';

export const createSendSlice: StateCreator<
  WhatsAppChatStore,
  [],
  [],
  SendSlice
> = (set, get) => ({
  pendingMessages: [],
  sendingMessage: false,
  uploadingMedia: false,

  sendTextMessage: async (text: string, mentionedJids?: string[]) => {
    const { wahaConfig, selectedChatId, replyingTo, pendingMessages } = get();
    if (!wahaConfig || !selectedChatId || !text.trim()) return;

    const tempId = `temp-${crypto.randomUUID()}`;
    const pending: PendingMessage = {
      tempId,
      chatId: selectedChatId,
      content: text.trim(),
      type: 'text',
      timestamp: new Date(),
      status: 'sending',
      retryCount: 0,
      replyToId: replyingTo?.id,
      replyToBody: replyingTo?.content,
    };

    // UI ottimistica
    set({
      pendingMessages: [...pendingMessages, pending],
      sendingMessage: true,
      replyingTo: null,
    });

    try {
      // Provider-aware: Cloud usa provider.sendText (Graph API via proxy), WAHA resta identico.
      const provider = get().provider;
      const isCloud = !!provider && provider.type !== 'whatsapp';
      const sendResult = isCloud && provider
        ? await provider.sendText(selectedChatId, text.trim(), { replyToId: replyingTo?.id })
        : await getWahaService().sendText(selectedChatId, text.trim(), replyingTo?.id, mentionedJids);
      set({ sendingMessage: false });

      // Sostituisci subito il pending con il Message reale costruito dalla risposta WAHA.
      // Il realtime arriverà più tardi, ma addMessage() rileverà l'ID duplicato e lo skipperà.
      // Niente più spinner di caricamento prolungato.
      if (sendResult?.id) {
        const realMessage: Message = {
          id: sendResult.id,
          chatId: selectedChatId,
          content: text.trim(),
          type: 'text',
          direction: 'outbound',
          status: 'sent', // WAHA ha accettato il messaggio → singolo spunta (NOWEB ritorna 'queued', non 'sent')
          timestamp: new Date(sendResult.timestamp ? sendResult.timestamp * 1000 : Date.now()),
          replyToId: replyingTo?.id,
          replyToBody: replyingTo?.content,
        };

        // Il messaggio va nell'elenco solo se la chat dell'invio e' ancora quella aperta:
        // altrimenti finirebbe nella chat sbagliata (lo mostreranno tempo reale o ricaricamento).
        set((state) => ({
          pendingMessages: state.pendingMessages.filter(p => p.tempId !== tempId),
          ...(state.selectedChatId === selectedChatId
            ? { messages: [...state.messages.filter(m => m.id !== realMessage.id), realMessage] }
            : {}),
          chats: applyMessageToChats(state.chats, realMessage),
        }));
        console.log('[WA] Send: pending replaced →', realMessage.id);
        // Aggiorna last_read_timestamp → il polling non mostrerà badge unread
        get().sendReadReceipt(selectedChatId);
      } else {
        console.warn('[WA] Send: sendResult.id missing, pending NOT replaced');
      }

      // Sender tracking: aggiorna sender_user_id per sessioni condivise
      if (sendResult?.id) {
        const { wahaConfig } = get();
        supabase.auth.getUser().then(({ data: { user: currentUser } }) => {
          if (currentUser?.id) {
            setTimeout(() => {
              supabase
                .from('whatsapp_messages')
                .update({ sender_user_id: currentUser.id })
                .eq('id', sendResult.id)
                .then(() => {});
            }, 2000);
          }
        });
      }
    } catch (error) {
      // Finestra di 24 ore chiusa (numero Ufficiale): il composer propone i modelli approvati.
      notifyIfWindowClosed(selectedChatId, error);
      // Marca come fallito
      const updated = get().pendingMessages.map((p) =>
        p.tempId === tempId
          ? { ...p, status: 'failed' as const, error: friendlyDbError(error) || 'Errore' }
          : p
      );
      set({ pendingMessages: updated, sendingMessage: false });
    }
  },

  sendMediaMessage: async (file: File, type: MessageType, caption?: string) => {
    const { wahaConfig, selectedChatId, pendingMessages, replyingTo } = get();
    if (!wahaConfig || !selectedChatId) return;
    const providerNow = get().provider;
    const isCloudNow = !!providerNow && providerNow.type !== 'whatsapp';
    // Cloud supporta la risposta citata anche sugli allegati (context.message_id).
    // WAHA no: li' il riferimento resta com'era.
    const replyTo = isCloudNow ? replyingTo : null;

    const tempId = `temp-${crypto.randomUUID()}`;
    const pending: PendingMessage = {
      tempId,
      chatId: selectedChatId,
      content: caption || file.name,
      type,
      mediaFile: file,
      mediaPreviewUrl: URL.createObjectURL(file),
      timestamp: new Date(),
      status: 'sending',
      retryCount: 0,
      replyToId: replyTo?.id,
      replyToBody: replyTo?.content,
    };

    set({
      pendingMessages: [...pendingMessages, pending],
      uploadingMedia: true,
      ...(replyTo ? { replyingTo: null } : {}),
    });

    try {
      // Provider-aware: Cloud usa provider.send* (upload a Meta via meta-cloud-proxy);
      // WAHA resta identico. Il servizio WAHA si chiede SOLO nel ramo WAHA: su un
      // numero Ufficiale non serve nessuna sessione WAHA caricata (prima
      // getWahaService() veniva chiamato comunque e gli allegati non partivano).
      const provider = providerNow;
      const isCloud = isCloudNow;
      const cloudOpts = replyTo?.id ? { replyToId: replyTo.id } : undefined;
      let sendResult: WahaSendResult;

      if (isCloud && provider) {
        switch (type) {
          case 'image':
            sendResult = await provider.sendImage(selectedChatId, file, caption, cloudOpts);
            break;
          case 'video':
            sendResult = await provider.sendVideo(selectedChatId, file, caption, cloudOpts);
            break;
          case 'audio':
          case 'ptt':
            // Il provider controlla il vero formato (Chrome registra WebM) e lo
            // converte in Ogg/Opus, l'unico accettato da Meta come vocale.
            sendResult = await provider.sendVoice(selectedChatId, file, cloudOpts);
            break;
          default:
            sendResult = await provider.sendFile(selectedChatId, file, caption, cloudOpts);
        }
      } else {
        const service = getWahaService();
        switch (type) {
          case 'image':
            sendResult = await service.sendImage(selectedChatId, file, caption);
            break;
          case 'video':
            sendResult = await service.sendVideo(selectedChatId, file, caption);
            break;
          case 'audio':
          case 'ptt':
            sendResult = await service.sendVoice(selectedChatId, file);
            break;
          default:
            sendResult = await service.sendFile(selectedChatId, file, caption);
        }
      }

      set({ uploadingMedia: false });

      // Sostituisci subito il pending con il Message reale costruito dalla risposta WAHA.
      // Allineato a sendTextMessage: spinner sparisce immediatamente, sidebar in cima istantanea.
      if (sendResult?.id) {
        const realMessage: Message = {
          id: sendResult.id,
          chatId: selectedChatId,
          content: caption || '',
          type,
          direction: 'outbound',
          status: 'sent', // WAHA ha accettato → singolo spunta
          timestamp: new Date(sendResult.timestamp ? sendResult.timestamp * 1000 : Date.now()),
          mediaUrl: pending.mediaPreviewUrl, // preserva blob URL locale per preview
          mediaMimetype: file.type || undefined,
          mediaFilename: type === 'document' ? file.name : undefined,
          replyToId: replyTo?.id,
          replyToBody: replyTo?.content,
        };
        // Il messaggio va nell'elenco solo se la chat dell'invio e' ancora quella aperta:
        // altrimenti finirebbe nella chat sbagliata (lo mostreranno tempo reale o ricaricamento).
        set((state) => ({
          pendingMessages: state.pendingMessages.filter(p => p.tempId !== tempId),
          ...(state.selectedChatId === selectedChatId
            ? { messages: [...state.messages.filter(m => m.id !== realMessage.id), realMessage] }
            : {}),
          chats: applyMessageToChats(state.chats, realMessage),
        }));
        // Aggiorna last_read_timestamp → il polling non mostrerà badge unread
        get().sendReadReceipt(selectedChatId);
      }
    } catch (error) {
      notifyIfWindowClosed(selectedChatId, error);
      const updated = get().pendingMessages.map((p) =>
        p.tempId === tempId
          ? { ...p, status: 'failed' as const, error: friendlyDbError(error) || 'Errore' }
          : p
      );
      set({ pendingMessages: updated, uploadingMedia: false });
    }
  },

  sendLocationMessage: async (lat: number, lng: number, title?: string, address?: string) => {
    const { wahaConfig, selectedChatId, provider } = get();
    if (!wahaConfig || !selectedChatId) return;
    if (provider && provider.type !== 'whatsapp') {
      // Numero Ufficiale: invio via Meta. L'errore risale al chiamante (toast).
      if (!provider.sendLocation) throw new Error('Invio della posizione non disponibile su questo numero.');
      let res;
      try {
        res = await provider.sendLocation(selectedChatId, lat, lng, title, address);
      } catch (error) {
        notifyIfWindowClosed(selectedChatId, error);
        throw error;
      }
      if (res?.id) get().sendReadReceipt(selectedChatId);
      return;
    }
    try {
      const service = getWahaService();
      await service.sendLocation(selectedChatId, lat, lng, title, address);
    } catch (error) {
      console.error('[ChatStore] sendLocation error:', error);
      throw error; // il chiamante mostra il toast
    }
  },

  sendContactMessage: async (contact: { name: string; phone: string }) => {
    const { wahaConfig, selectedChatId, provider } = get();
    if (!wahaConfig || !selectedChatId) return;
    if (provider && provider.type !== 'whatsapp') {
      // Numero Ufficiale: invio via Meta. L'errore risale al chiamante (toast).
      if (!provider.sendContact) throw new Error("Invio del contatto non disponibile su questo numero.");
      let res;
      try {
        res = await provider.sendContact(selectedChatId, contact);
      } catch (error) {
        notifyIfWindowClosed(selectedChatId, error);
        throw error;
      }
      if (res?.id) get().sendReadReceipt(selectedChatId);
      return;
    }
    try {
      const service = getWahaService();
      await service.sendContact(selectedChatId, contact);
    } catch (error) {
      console.error('[ChatStore] sendContact error:', error);
      throw error; // il chiamante mostra il toast
    }
  },

  sendPollMessage: async (name: string, options: string[], multipleAnswers = false) => {
    const { wahaConfig, selectedChatId, provider } = get();
    if (!wahaConfig || !selectedChatId) return;
    if (provider && provider.type !== 'whatsapp') {
      // Meta non ha i sondaggi nella Cloud API: errore esplicito invece di un finto invio.
      throw new Error('I sondaggi non sono disponibili sul numero Ufficiale.');
    }
    try {
      const service = getWahaService();
      await service.sendPoll(selectedChatId, name, options, multipleAnswers);
    } catch (error) {
      console.error('[ChatStore] sendPoll error:', error);
      throw error; // il chiamante mostra il toast
    }
  },

  removePendingMessage: (tempId: string) => {
    const { pendingMessages } = get();
    set({ pendingMessages: pendingMessages.filter((p) => p.tempId !== tempId) });
  },

  retryPendingMessage: async (tempId: string) => {
    const { pendingMessages, wahaConfig } = get();
    const pending = pendingMessages.find((p) => p.tempId === tempId);
    if (!pending || !wahaConfig) return;

    // Resetta status a sending
    const updated = pendingMessages.map((p) =>
      p.tempId === tempId ? { ...p, status: 'sending' as const, error: undefined, retryCount: p.retryCount + 1 } : p
    );
    set({ pendingMessages: updated });

    try {
      const provider = get().provider;
      const isCloud = !!provider && provider.type !== 'whatsapp';
      // Servizio WAHA solo per i numeri Standard (vedi sendMediaMessage).
      const service = () => getWahaService();
      const caption = pending.content || undefined;
      const cloudOpts = pending.replyToId ? { replyToId: pending.replyToId } : undefined;
      if (pending.type === 'text') {
        if (isCloud && provider) await provider.sendText(pending.chatId, pending.content, { replyToId: pending.replyToId });
        else await service().sendText(pending.chatId, pending.content, pending.replyToId);
      } else if (pending.mediaFile) {
        switch (pending.type) {
          case 'image':
            if (isCloud && provider) await provider.sendImage(pending.chatId, pending.mediaFile, caption, cloudOpts);
            else await service().sendImage(pending.chatId, pending.mediaFile, caption);
            break;
          case 'video':
            if (isCloud && provider) await provider.sendVideo(pending.chatId, pending.mediaFile, caption, cloudOpts);
            else await service().sendVideo(pending.chatId, pending.mediaFile, caption);
            break;
          case 'audio':
          case 'ptt':
            if (isCloud && provider) await provider.sendVoice(pending.chatId, pending.mediaFile, cloudOpts);
            else await service().sendVoice(pending.chatId, pending.mediaFile);
            break;
          default:
            if (isCloud && provider) await provider.sendFile(pending.chatId, pending.mediaFile, caption, cloudOpts);
            else await service().sendFile(pending.chatId, pending.mediaFile, caption);
        }
      }
    } catch (error) {
      notifyIfWindowClosed(pending.chatId, error);
      const failed = get().pendingMessages.map((p) =>
        p.tempId === tempId
          ? { ...p, status: 'failed' as const, error: friendlyDbError(error) || 'Errore' }
          : p
      );
      set({ pendingMessages: failed });
    }
  },
});
